import type { Action } from 'components/Layout/PaymentViewLayout';
import type { InvoiceStatus } from 'payments-api/model';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import { Badge } from '@fragment/ui/src/components/Badge/Badge';
import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import {
  kLabelColumnWidth,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';
import { StatusBadge } from '@fragment/ui/src/components/Payments/StatusBadge';

import { ToastProvider } from '../../../wrappers/ToastProvider';
import { DynamicInvoiceIcon } from './DynamicInvoiceIcon';

const kActionButtonWidth = 'w-fit text-xmd';

const ActionButtons = ({
  primaryAction,
  secondaryAction,
  tertiaryAction,
}: {
  primaryAction?: Action;
  secondaryAction?: Action;
  tertiaryAction?: Action;
}) => (
  <div
    className="flex flex-row gap-x-[0.25lh] w-fit justify-between"
    data-testid="invoice-action-buttons"
  >
    {tertiaryAction && (
      <SecondaryButtonV3
        onClick={tertiaryAction.onClick}
        className={kActionButtonWidth}
        disabled={tertiaryAction.disabled}
        disabledTooltipText={tertiaryAction.disabledTooltipText}
        data-testid={tertiaryAction['data-testid']}
      >
        {tertiaryAction.title}
      </SecondaryButtonV3>
    )}
    {secondaryAction && (
      <SecondaryButtonV3
        onClick={secondaryAction.onClick}
        className={kActionButtonWidth}
        disabled={secondaryAction.disabled}
        disabledTooltipText={secondaryAction.disabledTooltipText}
        data-testid={secondaryAction['data-testid']}
      >
        {secondaryAction.title}
      </SecondaryButtonV3>
    )}
    {primaryAction && (
      <PrimaryButtonV3
        onClick={primaryAction.onClick}
        className={kActionButtonWidth}
        disabled={primaryAction.disabled}
        disabledTooltipText={primaryAction.disabledTooltipText}
        data-testid={primaryAction['data-testid']}
      >
        {primaryAction.title}
      </PrimaryButtonV3>
    )}
  </div>
);

type Props = {
  onBack: () => void;
  title: string;
  primaryAction?: Action;
  secondaryAction?: Action;
  tertiaryAction?: Action;
  status: InvoiceStatus;
  issued: string;
  /** Total payin amount(s) rendered next to "Total" label (under Issued). */
  totalPayinContent?: React.ReactNode;
  setTitle?: (title: string) => void;
  /** Hex colors for the invoice icon gradient, one per user role. */
  iconColors?: string[];
  children: React.ReactNode;
};

export const InvoiceLayout = ({
  onBack,
  title,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  status,
  issued,
  totalPayinContent,
  setTitle,
  iconColors,
  children,
}: Props) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return undefined;

    let scrollParent: HTMLElement | null = el.parentElement;
    while (scrollParent) {
      const { overflow, overflowY } = getComputedStyle(scrollParent);
      if (
        ['auto', 'scroll'].includes(overflow) ||
        ['auto', 'scroll'].includes(overflowY)
      ) {
        break;
      }
      scrollParent = scrollParent.parentElement;
    }
    if (!scrollParent) return undefined;
    const parent = scrollParent;
    const handleScroll = () => {
      setIsScrolled(parent.scrollTop > 0);
    };
    parent.addEventListener('scroll', handleScroll, { passive: true });
    return () => parent.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <ToastProvider>
      <div
        className="flex flex-col w-full h-fit min-h-[100dvh] font-sans pb-f2"
        data-testid="invoice-layout"
      >
        {/* Sticky shrinking header group */}
        <div ref={stickyRef} className="sticky top-0 z-10 bg-canvas">
          {/* Page header bar — shrinks h-f8 → h-f4 */}
          <div
            className={clsx(
              'flex flex-row w-full justify-between border-b-[0.5px] border-main-300 px-f1',
              'transition-[height] duration-200 ease-in-out overflow-hidden',
              isScrolled ? 'h-f4 items-center' : 'h-f8 items-start pt-f1'
            )}
          >
            <div className="text-xmd text-main">
              <TextButton
                onClick={onBack}
                icon={<Icon type="left" size="sm" primaryColor="gray" />}
                hoverIcon={<Icon type="left" size="sm" primaryColor="black" />}
              />
            </div>
            {(primaryAction || secondaryAction || tertiaryAction) && (
              <ActionButtons
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                tertiaryAction={tertiaryAction}
              />
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="text-xmd font-helvetica flex flex-col flex-1 min-w-0">
          {/* Invoice title section — sticky below page header, shrinks h-f12 → h-f8 */}
          <div
            className={clsx(
              'flex w-full items-start border-b-[0.5px] border-main-200 bg-canvas sticky z-10',
              'transition-[height,top,padding] duration-200 ease-in-out overflow-hidden',
              isScrolled
                ? 'h-[108px] top-[calc(54px+0.5px)]'
                : 'h-[162px] top-[calc(108px+0.5px)]'
            )}
          >
            <div className="flex items-start gap-x-f1 px-[13.5px] flex-1 min-w-0 h-full">
              <Badge
                badge={
                  <DynamicInvoiceIcon
                    colors={iconColors ?? []}
                    height={24}
                    alwaysAnimate
                  />
                }
              >
                <TextCell
                  text={title}
                  textSize="lg"
                  align="left"
                  border="none"
                  onChange={setTitle}
                  data-testid="invoice-title-input"
                />
              </Badge>
            </div>
            <div className="flex flex-col h-full items-end justify-start p-[13.5px] shrink-0 border-l-[0.5px] border-main-200">
              <div className="w-fit shrink-0">
                <StatusBadge status={status} />
              </div>
            </div>
          </div>

          {/* Issued row */}
          <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
            <div
              className={clsx(
                'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
                kLabelColumnWidth
              )}
            >
              Issued
            </div>
            <div className="px-[13.5px] text-xmd text-main flex-1 font-helvetica">
              {new Date(issued).toDateString().split(' ').slice(1).join(' ')}
            </div>
          </div>

          {/* Total row: double height, upper-left justification */}
          <div className="flex items-start h-f8 w-full border-b-[0.5px] border-main-300">
            <div
              className={clsx(
                'px-[13.5px] pt-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full',
                kLabelColumnWidth
              )}
            >
              Amount
            </div>
            <div className="px-[13.5px] pt-[13.5px] text-xmd text-main flex-1 flex flex-wrap items-start gap-x-f2 gap-y-f1">
              {totalPayinContent}
            </div>
          </div>
          {children}
        </div>
      </div>
    </ToastProvider>
  );
};
